import React, { Component, ErrorInfo, ReactNode } from 'react';

interface IProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface IState {
  hasError: boolean;
}

class ErrorBoundary extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  public static getDerivedStateFromError(error: Error): IState {
    return {
      hasError: true,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      hasError: true,
    });

    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  public handleRetry = (): void => {
    this.setState({
      hasError: false,
    });
  }

  public handleGoHome = (): void => {
    window.location.href = '/';
  }

  public render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="error-boundary-container" style={styles.container as React.CSSProperties}>
          <div className="error-boundary-content" style={styles.content}>
            <div className="error-icon" style={styles.icon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M2.20164 18.4695L10.1643 4.00506C10.9021 2.66498 13.0979 2.66498 13.8357 4.00506L21.7984 18.4695C22.4443 19.6428 21.4598 21 19.9627 21H4.0373C2.54022 21 1.55571 19.6428 2.20164 18.4695Z" 
                  stroke="#f44336" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
                <path 
                  d="M12 9V13" 
                  stroke="#f44336" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
                <path 
                  d="M12 17.0195V17" 
                  stroke="#f44336" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
              </svg>
            </div>

            <h1 className="error-title" style={styles.title as React.CSSProperties}>
              Oops! Something went wrong
            </h1>

            <p className="error-message" style={styles.message}>
              We're sorry for the inconvenience. Our technical team has been notified and is working on a fix.
            </p>

            <div className="error-actions" style={styles.actions as React.CSSProperties}>
              <button
                onClick={this.handleRetry}
                className="btn btn-primary"
                style={styles.retryButton as React.CSSProperties}
              >
                Try Again
              </button>

              <button
                onClick={this.handleGoHome}
                className="btn btn-secondary"
                style={styles.homeButton as React.CSSProperties}
              >
                Go to Homepage
              </button>
            </div>

            <div className="error-help" style={styles.help as React.CSSProperties}>
              <p>If the problem persists, please contact technical support.</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  },
  content: {
    maxWidth: '600px',
    textAlign: 'center',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e0e0e0'
  },
  icon: {
    marginBottom: '24px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '16px',
    margin: '0 0 16px 0'
  },
  message: {
    fontSize: '16px',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '24px',
    margin: '0 0 24px 0'
  },
  details: {
    textAlign: 'left',
    marginBottom: '24px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: '#f9f9f9'
  },
  summary: {
    cursor: 'pointer',
    fontWeight: '600',
    color: '#333',
    marginBottom: '12px'
  },
  errorText: {
    fontSize: '12px',
    color: '#d32f2f',
    backgroundColor: '#ffebee',
    padding: '12px',
    borderRadius: '4px',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace'
  },
  actions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap'
  },
  retryButton: {
    backgroundColor: '#41b3f9',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '120px'
  },
  homeButton: {
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #ddd',
    padding: '12px 24px',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '120px'
  },
  help: {
    fontSize: '14px',
    color: '#888',
    borderTop: '1px solid #e0e0e0',
    paddingTop: '20px',
    margin: '0'
  }
};

export default ErrorBoundary;



// WEBPACK FOOTER //
// ./src/common/ErrorBoundary.tsx